import { tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import type {
  QueryCollectionMeta,
  SimpleComparison,
} from "@tanstack/query-db-collection";
import { fieldMatches } from "~/lib/db/collection-helpers";
import {
  isConcreteAssetDomainAssetId,
  normalizeAssetDomainAssetIds,
} from "./models";

export function preserveRequestedQueryRows<T extends { id: string }>(input: {
  collectionState: ReadonlyMap<string, T>;
  nextRows: readonly T[];
  requestedIds: readonly string[];
  mergeRow?: (existing: T, incoming: T) => T;
  /**
   * When true (default), requested ids that the server omitted from the
   * response are re-emitted from the cache so they are not deleted by the
   * subset write. Set to false when the server signals "no row for this id"
   * by omission and the caller wants the cache to reflect that — e.g. the
   * authoritative effective-metadata path where an unpublished override
   * should disappear from the asset collection.
   */
  preserveMissing?: boolean;
}): T[] {
  const mergedRows: T[] = [];
  const nextRowIds = new Set(input.nextRows.map((row) => row.id));

  for (const incoming of input.nextRows) {
    const existing = input.collectionState.get(incoming.id);
    mergedRows.push(
      existing && input.mergeRow
        ? input.mergeRow(existing, incoming)
        : incoming,
    );
  }

  if (input.preserveMissing === false) {
    return mergedRows;
  }

  const seenRequestedIds = new Set<string>();
  for (const requestedId of input.requestedIds) {
    if (seenRequestedIds.has(requestedId)) {
      continue;
    }
    seenRequestedIds.add(requestedId);

    if (nextRowIds.has(requestedId)) {
      continue;
    }

    const existingRow = input.collectionState.get(requestedId);
    if (!existingRow) {
      continue;
    }

    mergedRows.push(existingRow);
  }

  return mergedRows;
}

export function chunkArray<T>(values: readonly T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    return [Array.from(values)];
  }

  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(Array.from(values.slice(index, index + chunkSize)));
  }

  return chunks;
}

export function getAssetCollectionMeta(
  meta: unknown,
): QueryCollectionMeta | undefined {
  return meta as QueryCollectionMeta | undefined;
}

export function extractAssetIds(filters: SimpleComparison[]): string[] {
  const values = filters.flatMap((filter) => {
    if (!fieldMatches(filter, "id") && !fieldMatches(filter, "assetId")) {
      return [];
    }

    if (filter.operator === "eq" && typeof filter.value === "string") {
      return [filter.value];
    }

    if (filter.operator === "in" && Array.isArray(filter.value)) {
      return filter.value.filter(
        (value): value is string => typeof value === "string",
      );
    }

    return [];
  });

  // Both consumers of this feed batch endpoints that only speak concrete asset
  // ids, and one unaddressable entry throws for the entire request — so a
  // grouping handle or blueprint reference reaching a live-query filter used to
  // cost every other id in the batch its data. Drop them here rather than at
  // each call site: the filter values arrive from far-away callers (portfolio
  // rollups, strategy blueprints) that have no idea they are addressing a
  // token-keyed endpoint.
  return normalizeAssetDomainAssetIds(
    values.map((value) => tryAssetIdLikeToLegacyAssetId(value) ?? value),
  ).filter(isConcreteAssetDomainAssetId);
}
